import React, { useMemo } from 'react';
import { TimeSeriesLineChart, ChartEmptyState } from '@/components/visuals';
import type { ChartDensity } from '@/components/visuals';
import type { TrendsPoint } from '@/types/analyticsApi';

export interface PhaseProgressBurnupChartProps {
  points: TrendsPoint[];
  height?: number | string;
  showDataLabels: boolean;
  density?: ChartDensity;
  /** When series is empty, show this empty state (e.g. snapshot required). */
  emptyStateMessage?: string;
  emptyStateActionLabel?: string;
  emptyStateActionHref?: string;
}

export function PhaseProgressBurnupChart({
  points,
  height = 260,
  showDataLabels = true,
  density = 'overview',
  emptyStateMessage,
  emptyStateActionLabel,
  emptyStateActionHref,
}: PhaseProgressBurnupChartProps) {
  const { xAxisData, series } = useMemo(() => {
    const xAxisData = points.map((p) => p.uploaded_at ?? '').filter(Boolean);
    const phasesByWbs = new Map<string, { name: string; data: number[] }>();
    for (let i = 0; i < points.length; i++) {
      const phases = (points[i].metrics.phases as Array<{ wbs_id?: string; wbs_name?: string; pct_complete?: number }>) ?? [];
      for (const ph of phases) {
        const id = ph.wbs_id ?? '';
        if (!phasesByWbs.has(id)) {
          phasesByWbs.set(id, { name: ph.wbs_name ?? id, data: new Array(points.length).fill(0) });
        }
        phasesByWbs.get(id)!.data[i] = ph.pct_complete ?? 0;
      }
    }
    const series = Array.from(phasesByWbs.values());
    return { xAxisData, series };
  }, [points]);
  if (series.length === 0 && emptyStateMessage) {
    return (
      <ChartEmptyState
        message={emptyStateMessage}
        actionLabel={emptyStateActionLabel}
        actionHref={emptyStateActionHref}
      />
    );
  }
  return (
    <TimeSeriesLineChart
      xAxisData={xAxisData}
      series={series}
      height={height}
      showDataLabels={showDataLabels}
      density={density}
    />
  );
}
